import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import {
  downloadFile,
  openPdfInNewTab,
  printPDFBlob,
} from "@shared/utils/utils";
import { queryClient } from "@shared/api/queries/queryClient";
import { useToast } from "@shared/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import {
  approveBill,
  billDownload,
  billPrint,
  billPrintAndDeliver,
  downloadBillTemplate,
  getBillActivityLog,
  getBillData,
  getBillDetail,
  getBillDetails,
  getBillDraftDetails,
  getBillDraftStatus,
  getBillingReason,
  getBillTemplates,
  getConsumerCreditNote,
  getConsumerDetails,
  getConsumerFinancialSummary,
  getConsumerLastConsumptions,
  getConsumerLatestBill,
  getConsumers,
  getMeterAndConsumptions,
  getMeterDetails,
  getMeteReadingStatus,
  getPlanData,
  getServiceCharges,
  getSingleBillList,
  postApproveBill,
  submitConsumerSelection,
  updateConsumerSelection,
} from "./api";

export const useConsumers = (searchData: string) => {
  return useSmartQuery(
    [
      ...QueryKeyFactory.module.bx.singleBillGeneration.consumerList(),
      searchData,
    ],
    () => getConsumers(searchData),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      // enabled: searchData.trim().length > 2,
    }
  );
};

export const useUpdateConsumerSelection = (billId: any) => {
  const { toast } = useToast();
  return useSmartMutation(
    async (payload: any) => {
      return updateConsumerSelection(payload, billId);
    },
    {
      onError: (error: any) => {
        toast({ title: "Error", description: error.message });
      },
      onSuccess: async () => {
        await queryClient.refetchQueries({
          queryKey: [
            ...QueryKeyFactory.module.bx.singleBillGeneration.billData(),
            billId,
          ],
          exact: false,
        });
        await queryClient.refetchQueries({
          queryKey: [
            QueryKeyFactory.module.bx.billHistory.singleBillDraft(),
            1,
          ],
          exact: false,
        });
      },
    }
  );
};

export const useSubmitConsumerSelection = () => {
  const { toast } = useToast();
  return useSmartMutation(
    async (payload: any) => await submitConsumerSelection(payload),
    {
      onError: (error: any) => {
        toast({ title: "Error while creating the request" });
      },
      onSuccess: async () => {
        await queryClient.refetchQueries({
          queryKey: [
            QueryKeyFactory.module.bx.billHistory.singleBillDraft(),
            1,
          ],
          exact: false,
        });
      },
    }
  );
};

export const useBillTemplates = () => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.singleBillGeneration.billTemplates(),
    () => getBillTemplates(),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );
};

export const useBillingReason = () => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.singleBillGeneration.billReason(),
    () => getBillingReason()
  );
};

export const useBillData = (billId: any) => {
  return useSmartQuery(
    [...QueryKeyFactory.module.bx.singleBillGeneration.billData(), billId],
    () => getBillData(billId),
    {
      enabled: !!billId,
    }
  );
};

export const useConsumerCreditNote = (consumerId: any) => {
  return useSmartQuery(
    [
      ...QueryKeyFactory.module.bx.singleBillGeneration.consumerCreditNote(),
      consumerId,
    ],
    () => getConsumerCreditNote(consumerId),
    {
      enabled: !!consumerId,
    }
  );
};

export const useConsumerDetails = (billId: any) => {
  return useSmartQuery(
    [
      ...QueryKeyFactory.module.bx.singleBillGeneration.consumerDetails(),
      billId,
    ],
    () => getConsumerDetails(billId),
    {
      enabled: !!billId,
    }
  );
};

export const useConsumerLatestBill = (consumerNo: any) => {
  return useSmartQuery(
    [
      ...QueryKeyFactory.module.bx.singleBillGeneration.consumerLatestBill(),
      consumerNo,
    ],
    () => getConsumerLatestBill(consumerNo),
    {
      enabled: !!consumerNo,
    }
  );
};

export const usePlanData = (id: any) => {
  return useSmartQuery(
    [...QueryKeyFactory.module.bx.singleBillGeneration.planData(), id],
    () => getPlanData(id),
    {
      enabled: !!id,
    }
  );
};

export const useServiceCharges = (planId: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.singleBillGeneration.serviceCharges(planId),
    () => getServiceCharges(planId),
    {
      enabled: !!planId,
    }
  );
};

export const useMeterdata = (
  meterId: number[],
  invoiceStartDate: string,
  invoiceEndDate: string
) => {
  
  // Include the parameters in the query key to force refetching when they change
  return useSmartQuery(
    [
      QueryKeyFactory.module.bx.singleBillGeneration.billingMeters(),
      meterId,  // Add this to make the key unique based on meter ID
      invoiceStartDate,  // Add date parameters to the key
      invoiceEndDate
    ],
    () => getMeterDetails(meterId, invoiceStartDate, invoiceEndDate),
    {
      enabled: meterId.length > 0 && !!invoiceStartDate && !!invoiceEndDate,
      refetchOnWindowFocus: false,
      staleTime: 0
    }
  );
};

export const useMeterReadingStatus = () => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.singleBillGeneration.meterReadingStatus(),
    () => getMeteReadingStatus()
  );
};

export const useBillDraftStatus = () => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.singleBillGeneration.billDraftStatus(),
    () => getBillDraftStatus()
  );
};

export const useBillDetails = (billId: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.singleBillGeneration.billDetails(),
    () => getBillDetails(billId)
  );
};

export const useDownloadBill = () => {
  return useSmartMutation(
    async ({
      billId,
      filename = "template.pdf",
    }: {
      billId: string;
      filename?: string;
    }) => {
      const blob = await downloadBillTemplate(billId);
      downloadFile(blob, filename);
      return blob;
    }
  );
};

export const useViewBill = () => {
  return useSmartMutation(async (billId: string) => {
    const blob = await downloadBillTemplate(billId);
    console.log("Blob received:", blob);
    openPdfInNewTab(blob);
    return blob;
  });
};

export const usePrintBill = () => {
  return useSmartMutation(async (billId: string) => {
    const blob = await downloadBillTemplate(billId);
    printPDFBlob(blob);
    return blob;
  });
};

export const useApproveBill = (billId: any, draftId: string) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  return useSmartMutation(
    (payload: any) => {
      return approveBill(billId, draftId, payload);
    },
    {
      onError: (error: any) => {
        toast({ title: "Error While Approving the Bill" });
      },
      onSuccess: () => {
        navigate(`/bx/search?bill_run_id=${billId}`);
      },
    }
  );
};

export const useConsumerLastConsumption = (consumerNo: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.singleBillGeneration.billLastConsumption(consumerNo),
    () => getConsumerLastConsumptions(consumerNo)
  );
};

export const useConsumerFinancialSummary = (consumerNo: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.singleBillGeneration.billConsumerFinance(consumerNo),
    () => getConsumerFinancialSummary(consumerNo)
  );
};

export const useSingleBillList = (queryParams: Record<string, any>) => {
  return useSmartQuery(
    [QueryKeyFactory.module.bx.singleBillGeneration.singleBillList, queryParams],
    () => getSingleBillList(queryParams),{
      staleTime: 0,
    }
  );
};

export const useBillApprove = (billId) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  return useSmartMutation(
    (payload: any) => postApproveBill(billId, payload),
     {
      onSuccess: () => {
        navigate(`/bx/single-bill`);
      },
    }
  )
}

export const useBillDraftDetails = (remoteUtilityId, draftId) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.singleBillGeneration.singleBillDraft(draftId),
    () => getBillDraftDetails(remoteUtilityId, draftId),
    {
      enabled: !!remoteUtilityId && !!draftId,
    }
  );
};

export const usePrintBillAction = () => {
  return useSmartMutation(async (billId: string) => {
    const blob = await billPrint(billId);
    printPDFBlob(blob);
    return blob;
  });
};

export const useDownloadBillAction = () => {
  return useSmartMutation(
    async ({
      billId,
    }: {
      billId: string;
    }) => {
      const blob = await billDownload(billId);
      downloadFile(blob);
      return blob;
    }
  );
};

export const usePrintDeliverBillAction = () => {
  return useSmartMutation(async (billId: string) => {
    billPrintAndDeliver(billId);
  });
};

export const useBillDetail = (billId: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.singleBillGeneration.singleBillDetail(billId),
    () => getBillDetail(billId)
  );
};

export const useBillActivityLog = (billId: any) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.singleBillGeneration.singleBillActivityLog(billId),
    () => getBillActivityLog(billId)
  );
};

export const useMeterAndConsumption = (consumerNo: any, search: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.bx.singleBillGeneration.singleBillMeterConsumption(consumerNo, search),
    () => getMeterAndConsumptions(consumerNo, search)
  );
};